import { useState, useCallback, useRef } from 'preact/hooks'
import type { ToolTestResult } from '../services/toolTestRunner'
import { getToolTestSets, runToolTestSet, runAllToolTests } from '../services/toolTestRunner'
import type { Persona } from '../types'

interface ToolTestPanelProps {
  personas: Persona[]
  selectedPersona: Persona
}

export function ToolTestPanel({ personas, selectedPersona }: ToolTestPanelProps) {
  const [results, setResults] = useState<Map<string, ToolTestResult>>(new Map())
  const [running, setRunning] = useState<Set<string>>(new Set())
  const [selected, setSelected] = useState<Set<string>>(() => new Set(getToolTestSets().map(s => s.name)))
  const [runningAll, setRunningAll] = useState(false)
  const [personaId, setPersonaId] = useState(selectedPersona.id)
  const [expandedSet, setExpandedSet] = useState<string | null>(null)
  const [log, setLog] = useState<string[]>([])
  const logRef = useRef<HTMLDivElement>(null)

  const testSets = getToolTestSets()

  const appendLog = useCallback((line: string) => {
    setLog(prev => [...prev, line])
    // Auto-scroll log
    requestAnimationFrame(() => {
      if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight
    })
  }, [])

  const handleProgress = useCallback((setName: string, stepIndex: number, totalSteps: number, status: string, detail?: string) => {
    const prefix = `[${setName}] Step ${stepIndex + 1}/${totalSteps}`
    const msg = detail ? `${prefix}: ${status} - ${detail.slice(0, 120)}` : `${prefix}: ${status}`
    appendLog(msg)
  }, [appendLog])

  const handleRunSet = useCallback(async (setName: string) => {
    setRunning(prev => new Set(prev).add(setName))
    appendLog(`\n--- Starting: ${setName} (persona: ${personaId}) ---`)
    const result = await runToolTestSet(setName, personaId, handleProgress)
    setResults(prev => new Map(prev).set(setName, result))
    setRunning(prev => { const n = new Set(prev); n.delete(setName); return n })
    appendLog(`--- Finished: ${setName} - ${result.passed ? 'PASSED' : 'FAILED'} (${(result.duration / 1000).toFixed(1)}s) ---`)
  }, [personaId, handleProgress, appendLog])

  const handleRunSelected = useCallback(async () => {
    setRunningAll(true)
    setResults(new Map())
    setLog([])
    const selectedList = [...selected]
    appendLog(`Starting ${selectedList.length} test set(s) with persona: ${personaId}`)
    const allResults = await runAllToolTests(personaId, selectedList, handleProgress)
    for (const r of allResults) {
      setResults(prev => new Map(prev).set(r.name, r))
    }
    const passed = allResults.filter(r => r.passed).length
    const failed = allResults.length - passed
    appendLog(`\nAll done: ${passed} passed, ${failed} failed`)
    setRunningAll(false)
  }, [selected, personaId, handleProgress, appendLog])

  const toggleSelect = (name: string) => {
    setSelected(prev => {
      const n = new Set(prev)
      if (n.has(name)) n.delete(name); else n.add(name)
      return n
    })
  }

  const selectAll = () => setSelected(new Set(testSets.map(s => s.name)))
  const selectNone = () => setSelected(new Set())

  const passCount = [...results.values()].filter(r => r.passed).length
  const failCount = [...results.values()].filter(r => !r.passed).length
  const totalSteps = [...results.values()].reduce((sum, r) => sum + r.steps.length, 0)
  const passedSteps = [...results.values()].reduce((sum, r) => sum + r.steps.filter(s => s.passed).length, 0)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ padding: '16px 20px', borderBottom: '1px solid rgba(250,250,250,0.1)', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <h3 style={{ fontSize: 16, margin: 0 }}>AI Tool Tests</h3>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {results.size > 0 && (
              <span style={{ fontSize: 12, color: 'rgba(250,250,250,0.5)' }}>
                Sets: {passCount}/{results.size} | Steps: {passedSteps}/{totalSteps}
              </span>
            )}
            <button
              style={btnStyle(true)}
              onClick={handleRunSelected}
              disabled={runningAll || selected.size === 0}
            >
              {runningAll ? 'Running...' : `Run Selected (${selected.size})`}
            </button>
          </div>
        </div>

        {/* Persona selector */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
          <span style={{ color: 'rgba(250,250,250,0.5)' }}>Persona:</span>
          <select
            value={personaId}
            onChange={(e) => setPersonaId((e.target as HTMLSelectElement).value)}
            style={selectStyle}
            disabled={runningAll}
          >
            {personas.map(p => (
              <option key={p.id} value={p.id}>{p.emoji} {p.name}</option>
            ))}
          </select>
          <span style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
            <button style={linkBtnStyle} onClick={selectAll}>Select all</button>
            <button style={linkBtnStyle} onClick={selectNone}>Select none</button>
          </span>
        </div>
      </div>

      {/* Test set list + log split */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {/* Left: test sets */}
        <div style={{ flex: 1, overflow: 'auto', padding: '12px 20px' }}>
          {testSets.map(ts => {
            const result = results.get(ts.name)
            const isRunning = running.has(ts.name)
            const isExpanded = expandedSet === ts.name
            const isSelected = selected.has(ts.name)

            return (
              <div key={ts.name} style={{ marginBottom: 8 }}>
                <div style={setHeaderStyle}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleSelect(ts.name)}
                      disabled={runningAll}
                    />
                    <span style={{ fontSize: 15 }}>
                      {isRunning ? '\u23F3' : result ? (result.passed ? '\u2705' : '\u274C') : '\u26AA'}
                    </span>
                    <span style={{ fontWeight: 500, fontSize: 13 }}>{ts.name}</span>
                  </label>
                  {result && (
                    <span style={{ fontSize: 11, color: 'rgba(250,250,250,0.4)', marginRight: 8 }}>
                      {result.steps.filter(s => s.passed).length}/{result.steps.length} steps | {(result.duration / 1000).toFixed(1)}s
                    </span>
                  )}
                  <button
                    style={btnStyle(false)}
                    onClick={() => handleRunSet(ts.name)}
                    disabled={isRunning || runningAll}
                  >
                    {isRunning ? '...' : 'Run'}
                  </button>
                  {result && (
                    <button
                      style={{ ...linkBtnStyle, marginLeft: 4 }}
                      onClick={() => setExpandedSet(isExpanded ? null : ts.name)}
                    >
                      {isExpanded ? 'Hide' : 'Details'}
                    </button>
                  )}
                </div>
                <div style={{ fontSize: 11, color: 'rgba(250,250,250,0.35)', marginLeft: 52, marginTop: 2 }}>
                  {ts.description}
                </div>

                {/* Expanded step details */}
                {isExpanded && result && (
                  <div style={{ marginLeft: 24, marginTop: 8 }}>
                    {result.steps.map((step, i) => (
                      <div key={i} style={stepStyle(step.passed)}>
                        <div style={{ display: 'flex', gap: 6, alignItems: 'baseline' }}>
                          <span>{step.passed ? '\u2705' : '\u274C'}</span>
                          <span style={{ fontSize: 12, fontWeight: 500, color: 'rgba(250,250,250,0.7)' }}>
                            Step {i + 1}
                          </span>
                          <span style={{ fontSize: 11, color: 'rgba(250,250,250,0.35)' }}>
                            {(step.duration / 1000).toFixed(1)}s
                          </span>
                        </div>
                        <div style={{ fontSize: 12, color: 'rgba(250,250,250,0.5)', marginTop: 4, fontStyle: 'italic' }}>
                          {step.prompt.slice(0, 150)}{step.prompt.length > 150 ? '...' : ''}
                        </div>
                        {step.toolResults.length > 0 && (
                          <div style={{ fontSize: 11, color: '#4fc3f7', marginTop: 2 }}>
                            Tools: {step.toolResults.map(t => `${t.tool}/${t.action}`).join(', ')}
                          </div>
                        )}
                        <div style={{ fontSize: 12, color: 'rgba(250,250,250,0.6)', marginTop: 4, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                          {step.responseText.slice(0, 300)}{step.responseText.length > 300 ? '...' : ''}
                        </div>
                        {step.error && (
                          <div style={{ fontSize: 12, color: '#ff6b6b', marginTop: 4 }}>
                            {step.error}
                          </div>
                        )}
                      </div>
                    ))}
                    {result.error && (
                      <div style={{ fontSize: 12, color: '#ff6b6b', marginTop: 8 }}>
                        Set error: {result.error}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>

        {/* Right: console log */}
        <div style={{
          width: 380,
          borderLeft: '1px solid rgba(250,250,250,0.1)',
          display: 'flex',
          flexDirection: 'column',
          flexShrink: 0,
        }}>
          <div style={{
            padding: '8px 12px',
            borderBottom: '1px solid rgba(250,250,250,0.1)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: 'rgba(250,250,250,0.5)' }}>Console Log</span>
            <button style={linkBtnStyle} onClick={() => setLog([])}>Clear</button>
          </div>
          <div
            ref={logRef}
            style={{
              flex: 1,
              overflow: 'auto',
              padding: 10,
              fontFamily: 'monospace',
              fontSize: 11,
              lineHeight: '1.5',
              color: 'rgba(250,250,250,0.6)',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-all',
            }}
          >
            {log.length === 0
              ? <span style={{ color: 'rgba(250,250,250,0.3)' }}>Run tests to see console output here.{'\n'}Full details also logged to browser console (F12).</span>
              : log.map((line, i) => (
                  <div key={i} style={{
                    color: line.includes('FAIL') || line.includes('failed')
                      ? '#ff6b6b'
                      : line.includes('PASS') || line.includes('passed')
                        ? '#66bb6a'
                        : line.startsWith('---') || line.startsWith('===')
                          ? 'rgba(250,250,250,0.4)'
                          : undefined,
                  }}>
                    {line}
                  </div>
                ))
            }
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Styles ──────────────────────────────────────────────────────

const btnStyle = (primary: boolean): Record<string, string | number> => ({
  padding: '5px 14px',
  border: primary ? 'none' : '1px solid rgba(250,250,250,0.15)',
  borderRadius: 6,
  background: primary ? 'rgba(45,165,160,0.3)' : 'transparent',
  color: primary ? '#fff' : 'rgba(250,250,250,0.6)',
  cursor: 'pointer',
  fontSize: 12,
  fontWeight: primary ? 600 : 400,
})

const linkBtnStyle: Record<string, string | number> = {
  padding: '2px 6px',
  border: 'none',
  background: 'transparent',
  color: 'rgba(79,195,247,0.7)',
  cursor: 'pointer',
  fontSize: 11,
  textDecoration: 'underline',
}

const selectStyle: Record<string, string | number> = {
  padding: '4px 8px',
  background: 'rgba(0,51,68,0.8)',
  border: '1px solid rgba(250,250,250,0.15)',
  borderRadius: 6,
  color: '#fafafa',
  fontSize: 13,
}

const setHeaderStyle: Record<string, string | number> = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '8px 10px',
  background: 'rgba(0,51,68,0.4)',
  borderRadius: 6,
}

const stepStyle = (passed: boolean): Record<string, string | number> => ({
  padding: '8px 10px',
  marginBottom: 4,
  background: passed ? 'rgba(68,204,68,0.06)' : 'rgba(255,68,68,0.06)',
  borderRadius: 6,
  borderLeft: `3px solid ${passed ? '#66bb6a' : '#ff6b6b'}`,
})
